import { mockDelay, mockHttpError } from './withMockFallback';

/**
 * Mock зон камеры (Part 4, разделы 6.5–6.8). DTO повторяет ТЗ 1:1.
 *
 * `zone_id` выдаёт «backend» и он не меняется при правке геометрии — именно то
 * свойство, ради которого зоны уезжают из detection_configs, где идентичностью
 * был индекс массива.
 *
 * Форма списка — production-конверт `{ camera_id, zones }`; нормализацию в
 * `{ items }` делает адаптер сервиса, а не мок.
 */

const createZones = (cameraId) => ({
  camera_id: Number(cameraId),
  zones: [
    {
      zone_id: 'zone-a',
      display_name: 'Зона упаковки',
      polygon: [[10, 10], [100, 10], [100, 100], [10, 100], [10, 10]],
      version: 3,
      used_by_rules: [{ rule_id: 'r2', name: 'Без головного убора' }],
    },
  ],
});

const store = new Map();
let idCounter = 0;

const getState = (cameraId) => {
  const key = String(cameraId);
  if (!store.has(key)) store.set(key, createZones(cameraId));
  return store.get(key);
};

const findZone = (state, zoneId) => state.zones.find((zone) => zone.zone_id === zoneId);

const notFound = () => mockHttpError(404, { detail: 'zone_not_found' });

export const listZonesMock = (cameraId) => mockDelay(getState(cameraId));

export const createZoneMock = (cameraId, payload = {}) => {
  const state = getState(cameraId);
  idCounter += 1;

  const zone = {
    zone_id: `zone-${idCounter}-${Date.now().toString(36)}`,
    display_name: payload.display_name || 'Новая зона',
    polygon: payload.polygon || [],
    version: 1,
    used_by_rules: [],
  };

  state.zones = [...state.zones, zone];
  return mockDelay(zone);
};

export const updateZoneMock = (cameraId, zoneId, payload = {}) => {
  const state = getState(cameraId);
  const zone = findZone(state, zoneId);
  if (!zone) return Promise.reject(notFound());

  if (payload.expected_version !== zone.version) {
    return Promise.reject(mockHttpError(409, {
      detail: 'zone_version_conflict',
      current_version: zone.version,
    }));
  }

  // zone_id при обновлении не меняется — это и есть смысл стабильного id.
  const updated = {
    ...zone,
    display_name: payload.display_name ?? zone.display_name,
    polygon: payload.polygon ?? zone.polygon,
    version: zone.version + 1,
  };

  state.zones = state.zones.map((item) => (item.zone_id === zoneId ? updated : item));
  return mockDelay(updated);
};

export const deleteZoneMock = (cameraId, zoneId, { expectedVersion = null, force = false } = {}) => {
  const state = getState(cameraId);
  const zone = findZone(state, zoneId);
  if (!zone) return Promise.reject(notFound());

  if (expectedVersion != null && expectedVersion !== zone.version) {
    return Promise.reject(mockHttpError(409, {
      detail: 'zone_version_conflict',
      current_version: zone.version,
    }));
  }

  // FE-Z06: без force занятая зона не удаляется — backend отвечает 409 со
  // списком зависимых правил; c force удаление проходит, каскад на правила
  // мок не выдумывает (их деактивацию делает backend).
  if (zone.used_by_rules.length > 0 && !force) {
    return Promise.reject(mockHttpError(409, {
      detail: { code: 'zone_used_by_active_rules', dependent_rules: zone.used_by_rules },
    }));
  }

  state.zones = state.zones.filter((item) => item.zone_id !== zoneId);

  return mockDelay({ status: 'deleted', zone_id: zoneId });
};

/** Только для тестов. */
export const resetZonesMock = () => store.clear();
